import { Tooltip } from "@material-ui/core";
import classnames from "classnames";
import { clone, contains, join, map } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import {
  addUserToReadByForComments,
  AddUserToReadByForCommentsType
} from "src/commentView/actions";
import { AddCommentIcon } from "src/commentView/AddCommentIcon";
import { CommentIcon } from "src/commentView/CommentIcon";
import { CommentPopover } from "src/commentView/CommentPopover";
import {
  getUnreadComments,
  sortCommentsByPeriod
} from "src/commentView/helpers";
import { Comment, CommentsByPeriod } from "src/commentView/types";
import { getUserId } from "src/login/selector";
import { State } from "src/state";
import { ChartOfAccountsPresenter } from "../model/presenter/types";
import {
  getComments,
  getLanguage,
  getSpreadId
} from "../model/spread/selector";
import { RuleChangeLog, RuleHistory } from "../model/spread/types";
import ChildAccountsSection from "./ChildAccountsSection";

interface CashFlowProps {
  chartOfAccounts: ChartOfAccountsPresenter;
  ruleHistory: RuleHistory;
  initialChangelog: RuleChangeLog;
  spreadId: string;
  comments: Comment[] | null;
  userId: string;
  addUserToReadByForComments: AddUserToReadByForCommentsType;
  language: string | null;
}

interface CashFlowState {
  spreadComments: CommentsByPeriod;
  anchorElByPeriod: { [period: string]: HTMLElement | null };
}

export class CashFlow extends React.Component<CashFlowProps, CashFlowState> {
  public containerClasses = join(" ", [
    "ChartOfAccounts__container BalanceSheet__container"
  ]);

  constructor(props: CashFlowProps) {
    super(props);

    this.state = {
      spreadComments: {},
      anchorElByPeriod: {}
    };
  }
  public componentDidMount() {
    const { comments } = this.props;

    if (!comments) {
      return;
    }

    const spreadComments = sortCommentsByPeriod(comments);

    this.setState({ spreadComments });
  }

  public render() {
    const commentPeriods = this.props.chartOfAccounts.periods || 1;

    return (
      <div className={this.containerClasses}>
        <div
          className={`AccountRow__container AccountRow__container--header AccountRow__container--period-${this.props.chartOfAccounts.periods.length}`}
        >
          <div className="AccountRow__name AccountRow__name--header" />
          {this.props.chartOfAccounts.periods.map((period, index) => (
            <div
              className={`AccountRow__value AccountRow__value--header`}
              key={period}
            >
              <div className="AccountRow__value--header--comment--icon">
                <div>{period}</div>

                <span
                  className="AccountRow__value--comment-button"
                  onClick={this.openCommentPopover(commentPeriods[index])}
                >
                  {this.hasCommentsForPeriod(commentPeriods[index]) ? (
                    <React.Fragment>
                      {this.renderReadStatus(commentPeriods[index])}
                      <CommentIcon />
                    </React.Fragment>
                  ) : (
                    <AddCommentIcon fill="white" />
                  )}
                </span>
              </div>

              <Tooltip
                classes={{ tooltip: "ToolTip-container" }}
                title="Add Comment"
                placement="top"
              >
                <React.Fragment>
                  <CommentPopover
                    key={period}
                    comments={
                      this.state.spreadComments[commentPeriods[index]] || []
                    }
                    shouldHideInput={false}
                    isOpen={Boolean(
                      this.state.anchorElByPeriod[commentPeriods[index]]
                    )}
                    spreadId={this.props.spreadId}
                    onClose={this.handlePopoverClose(commentPeriods[index])}
                    anchorEl={
                      this.state.anchorElByPeriod[commentPeriods[index]]
                    }
                    updateComments={this.updateComments(commentPeriods[index])}
                    period={commentPeriods[index]}
                  />
                </React.Fragment>
              </Tooltip>
            </div>
          ))}
        </div>
        <ChildAccountsSection
          childAccounts={this.props.chartOfAccounts.children}
          hierarchyLevel={1}
          ruleHistory={this.props.ruleHistory}
          initialChangelog={this.props.initialChangelog}
          periods={commentPeriods}
        />
      </div>
    );
  }

  private handlePopoverClose = (period: string) => (
    event?: React.SyntheticEvent<{}>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = null;

    const spreadComments = clone(this.state.spreadComments);
    const comments = spreadComments[period];
    if (comments) {
      const unreadComments = getUnreadComments(comments, this.props.userId);

      this.props.addUserToReadByForComments({
        comments: unreadComments,
        userId: this.props.userId
      });

      const commentsMarkedAsRead = map((comment: Comment) => {
        if (comment.readBy && !contains(this.props.userId, comment.readBy)) {
          comment.readBy.push(this.props.userId);
        }
        return comment;
      }, comments);

      spreadComments[period] = commentsMarkedAsRead;
    }

    this.setState({
      anchorElByPeriod,
      spreadComments
    });
  };

  private openCommentPopover = (period: string) => (
    event: React.MouseEvent<HTMLElement>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = event.currentTarget;

    this.setState({
      anchorElByPeriod
    });
  };

  private hasUnreadComments = (comments: Comment[]) =>
    getUnreadComments(comments, this.props.userId).length > 0;

  private renderReadStatus = (period: string) => {
    const hasUnread = this.hasUnreadComments(this.state.spreadComments[period]);

    return (
      <div
        className={classnames(
          "AccountRow__read-status",
          {
            "AccountRow__read-status--unread": hasUnread
          },
          {
            "AccountRow__read-status--read": !hasUnread
          }
        )}
      />
    );
  };

  private updateComments = (period: string) => (comments: Comment[]) => {
    const spreadComments = clone(this.state.spreadComments);

    spreadComments[period] = comments;

    this.setState({
      spreadComments
    });
  };

  private hasCommentsForPeriod = (period: string) => {
    return (
      this.state.spreadComments[period] &&
      this.state.spreadComments[period].length > 0
    );
  };
}

const mapStateToProps = (state: State, ownProps: any) => ({
  spreadId: getSpreadId(state),
  userId: getUserId(state),
  comments: getComments(state),
  language: getLanguage(state)
});

export default connect(
  mapStateToProps,
  {
    addUserToReadByForComments: addUserToReadByForComments.action
  }
)(CashFlow);



// WEBPACK FOOTER //
// ./src/spread/chartOfAccounts/CashFlow.tsx